import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import {
  TFunction,
  formatNumber,
  formatNumericCurrency,
  isFeatureActiveOnCurrentFarm,
  useDeleteField,
  useFieldGeometry,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldDetails,
  useTranslation,
} from "utils";

import { CARGILL_DE_CARBON_PROGRAM_ID, FEATURE_FLAG_SLUGS } from "consts";

import { ACCOUNTING_METHODOLOGY, CarbonProgram, ENROLLMENT_TYPE } from "models";

import {
  Button,
  Card,
  ErrorBoundary,
  ExtraInfo,
  FCPOMenu,
  FieldParticipationTags,
  Flex,
  HideFCPOFeatureForUnenrolledFarms,
  Icon,
  MapStatic,
  Modal,
  OpenTaskButton,
  Popover,
  SoilPlus3Menu,
  Spin,
  Tag,
  Tooltip,
  TooltipWrapper,
  Typography,
} from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { useSoilSightFieldImport } from "pages/app/scenario-analysis/hooks/useSoilSightFieldImport";

type FieldCellProps = {
  activateCheckbox?: boolean;
  areOutcomesValid: boolean;
  className?: string;
  currentPayout: number | null;
  dataTestId?: string;
  fieldId: string;
  fieldName: string;
  hasFieldOpenTasks?: boolean;
  hasFieldOutcomes: boolean;
  hasLocation: boolean;
  hasNoAllotment?: boolean;
  inView?: boolean;
  isEnrolled?: boolean;
  hasNoPayoutDueToNoPoints?: boolean;
  isRedZone: boolean;
  openTasksCount?: number;
  shouldShowPayout: boolean;
  size: number;
  onClick?: () => void;
  onMissingDataClick: () => void;
  onOpenFieldDetails: () => void;
};

type OpenTaskUIProps = {
  carbonProgram: CarbonProgram | null;
  carbonProgramOutcomesProgress: number;
  currentPayout: number | null;
  handleMissingDataClick: (e: MouseEvent<HTMLElement>) => void;
  hasNoAllotment: boolean;
  isEnrolled: boolean;
  isFetchingCarbonProgramOutcomes: boolean;
  openTasksCount?: number;
  setOpenProgramInfoDrawer: (visible: boolean) => void;
  shouldShowPayout: boolean;
  t: TFunction;
};

const getCellStates = (name: string, hasLocation: boolean, t: TFunction<["farm", "allFieldView"], undefined>) => {
  if (!name) {
    return {
      nameError: t("farm:unknownFieldName"),
      iconName: "klim/edit-element",
      hasError: true,
      className: "red-text",
    };
  }
  if (!hasLocation) {
    return {
      locationError: t("farm:unknownLocation"),
      iconName: "klim/map-pin-plus",
      hasError: true,
      className: "red-text",
    };
  }

  return {
    nameError: null,
    locationError: null,
    size: null,
    iconName: null,
    hasError: false,
    className: null,
  };
};

const OpenTaskUI = ({
  carbonProgram,
  carbonProgramOutcomesProgress,
  currentPayout,
  handleMissingDataClick,
  hasNoAllotment,
  isEnrolled,
  isFetchingCarbonProgramOutcomes,
  openTasksCount,
  setOpenProgramInfoDrawer,
  shouldShowPayout,
  t,
}: OpenTaskUIProps) => {
  if (openTasksCount) {
    return (
      <Flex align="center" gap={8}>
        <OpenTaskButton
          count={openTasksCount}
          text={t("allFieldView:fieldCell.addData")}
          isLoading={isFetchingCarbonProgramOutcomes || carbonProgramOutcomesProgress !== 100}
          onClick={handleMissingDataClick}
          dataTestId="add-data-btn"
        />
      </Flex>
    );
  }

  if (carbonProgram?.fieldsManagement?.fieldEnrollmentType === ENROLLMENT_TYPE.auto || (hasNoAllotment && isEnrolled)) {
    return (
      <Flex align="center" gap={8}>
        <Icon iconName={"tabler/check"} className={"icon inline checkmark-icon"} size="s" />
      </Flex>
    );
  }

  if (carbonProgram?.accountingMethodology === "intervention" && isEnrolled && shouldShowPayout) {
    return (
      <Flex align="center" gap={8}>
        {currentPayout !== null && (
          <Flex gap={4}>
            <Typography
              variant="paragraph-s"
              bold
              data-testid="field-payouts"
              color="green-400"
              {...(carbonProgram?.id === CARGILL_DE_CARBON_PROGRAM_ID && {
                onClick: (e) => {
                  e.stopPropagation();
                  setOpenProgramInfoDrawer(true);
                },
              })}
            >
              {formatNumericCurrency(currentPayout, { currency: carbonProgram?.currency })}
            </Typography>
            {carbonProgram?.id === CARGILL_DE_CARBON_PROGRAM_ID && (
              <Icon
                iconName="tabler/info-circle"
                size="xs"
                className="field-cell-payout-info-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenProgramInfoDrawer(true);
                }}
              />
            )}
          </Flex>
        )}
      </Flex>
    );
  }

  return null;
};

/** Marks an enrolled field the program can't calculate a payout for. It doesn't block the submission. Shared with the table view's field cell. */
export const NoPayoutTag = ({ className }: { className?: string }) => {
  const { t } = useTranslation(["allFieldView"]);

  return (
    <Tooltip title={t("allFieldView:fieldCell.noPayoutTooltip")}>
      <Flex>
        <Tag className={className} colour="yellow" text={t("allFieldView:fieldCell.noPayoutTag")} dataTestId="field-no-payout-tag" />
      </Flex>
    </Tooltip>
  );
};

export const FieldCell = ({
  areOutcomesValid = false,
  className = "",
  currentPayout,
  fieldId,
  fieldName,
  hasFieldOpenTasks,
  hasFieldOutcomes = false,
  hasLocation,
  hasNoAllotment = false,
  isEnrolled = false,
  hasNoPayoutDueToNoPoints = false,
  isRedZone,
  openTasksCount,
  shouldShowPayout,
  size,
  onClick: _onClick,
  onMissingDataClick,
  onOpenFieldDetails,
}: FieldCellProps) => {
  const { t } = useTranslation(["farm"]);
  const { currentFarm } = useGetFarms();
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { data: allFieldsPractices, isLoading: isLoadingAllFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: field } = useGetFieldDetails({ id: fieldId });
  const { mutate: deleteField } = useDeleteField();
  const { geometry } = useFieldGeometry(fieldId);
  const { isFetching: isFetchingCarbonProgramOutcomes, progress: carbonProgramOutcomesProgress } = useGetAllCarbonProgramOutcomes(
    currentFarm?.id,
    currentFarm?.carbonProgram
  );

  const { isSubmissionFeatureEnabled } = useSubmissionStatus();
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  const { isSoilSightIntegrationActive, canSubmitSoilSightImport, isSoilSightImportSubmitting, isFetchingPracticeCropsForSoilSight, handleSoilSightCTAClick } =
    useSoilSightFieldImport(fieldId);

  const soilSightPopoverContent = (
    <div className="field-cell-soilsight-popup" onClick={(e) => e.stopPropagation()}>
      <div className="field-cell-soilsight-popup-header">
        <Icon iconName="klim/magic" height={18} width={18} className="field-cell-soilsight-popup-logo-icon" />
        <Typography variant="paragraph-s" bold className="field-cell-soilsight-popup-brand">
          {t("farm:soilSightPopup.brand", "SOILSIGHT")}
        </Typography>
      </div>
      <Typography variant="paragraph-l" bold className="field-cell-soilsight-popup-title">
        {t("farm:soilSightPopup.title", "Plan your rotation. Know your margins.")}
      </Typography>
      <ul className="field-cell-soilsight-popup-list">
        <li>
          <Icon iconName="tabler/circle-check" size="s" />
          <Typography variant="paragraph-s">{t("farm:soilSightPopup.bullet1", "Recommendations for your next crop rotation")}</Typography>
        </li>
        <li>
          <Icon iconName="tabler/circle-check" size="s" />
          <Typography variant="paragraph-s">{t("farm:soilSightPopup.bullet2", "Regenerative practices with real impact")}</Typography>
        </li>
        <li>
          <Icon iconName="tabler/circle-check" size="s" />
          <Typography variant="paragraph-s">{t("farm:soilSightPopup.bullet3", "See the financial effect before you commit")}</Typography>
        </li>
      </ul>
      <Button
        type="primary"
        onClick={handleSoilSightCTAClick}
        dataTestId="soilsight-popup-cta"
        disabled={!canSubmitSoilSightImport || isFetchingPracticeCropsForSoilSight}
        loading={isSoilSightImportSubmitting}
      >
        {t("farm:soilSightPopup.cta", "See recommendations")} <Icon iconName="tabler/arrow-up-right" size="s" color="white" />
      </Button>
    </div>
  );

  const SoilSightMagicIcon = isSoilSightIntegrationActive ? (
    <Popover content={soilSightPopoverContent} trigger="hover" placement="right" className="field-cell-soilsight-popover">
      <span className="field-cell-soilsight-icon" data-testid="soilsight-magic-icon" onClick={handleSoilSightCTAClick}>
        <Icon iconName="klim/magic" height={20} width={20} />
      </span>
    </Popover>
  ) : null;

  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [openProgramInfoDrawer, setOpenProgramInfoDrawer] = useState(false);

  const practices = allFieldsPractices?.[fieldId];
  const fieldHasActivePractices = !!practices?.availablePracticeBundles.some((bundle) => bundle.active);
  const cellStates = useMemo(() => getCellStates(fieldName, hasLocation, t), [fieldName, hasLocation, t]);
  const fieldNumberLabel = field?.number ? t("farm:fieldCellNumber", { number: field.number }) : "";
  const hectareLimitWouldBeExceeded = !field || (field.exceedsHectareLimit && !fieldHasActivePractices);
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const isPracticeEnrollmentDisabled =
    currentFarm?.carbonProgram?.fieldsManagement?.fieldEnrollmentType !== ENROLLMENT_TYPE.auto && (hectareLimitWouldBeExceeded || !currentFarm?.carbonProgram);

  const handleMissingDataClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // if there are field related open tasks, we want to resolve those before navigating to the crops with missing data.
    if (isSubmissionFeatureEnabled && hasFieldOpenTasks) {
      onOpenFieldDetails();
      return;
    }
    onMissingDataClick();
  };

  const onModalSubmit = useCallback(() => {
    deleteField({
      fieldId: fieldId || "",
    });

    return setShowDeleteFieldModal(false);
  }, [deleteField, fieldId]);

  const newSubmissionFlowFieldCell = (
    <div className="field-cell-layout">
      <Flex className="field-cell-layout-main" gap={"0.25rem"}>
        <Flex className="field-cell-layout-top" justify="space-between">
          <Flex vertical={true} justify="flex-start" className="field-cell-info">
            <TooltipWrapper
              className="field-cell-tooltip"
              tooltip={
                isPracticeEnrollmentDisabled
                  ? {
                      title: (
                        <Typography variant="paragraph-m" className="field-cell-tooltip-content">
                          {hectareLimitWouldBeExceeded ? t("allFieldView:fieldCell.tooltipHaLimit") : t("allFieldView:fieldCell.tooltipPayout")}
                        </Typography>
                      ),
                      placement: "top",
                    }
                  : null
              }
              data-testid={"field-cell-tooltip"}
            >
              <Typography variant="paragraph-s" bold className={`${cellStates.nameError ? cellStates.className : ""} field-name`}>
                {fieldName || cellStates.nameError}
              </Typography>
            </TooltipWrapper>
            <Flex>
              {cellStates.locationError ? (
                <Typography variant="paragraph-m" className={cellStates.locationError ? cellStates.className : ""}>
                  {cellStates.locationError}
                </Typography>
              ) : (
                <Flex align="center" gap="0.5rem" className="field-cell-size-and-number">
                  <Typography variant="paragraph-s" color="grey-600" className="margin-0" noWrap>
                    {formatNumber({ number: size })} ha
                  </Typography>
                  {fieldNumberLabel && (
                    <Typography variant="paragraph-s" color="grey-600" className="margin-0 field-number">
                      {fieldNumberLabel}
                    </Typography>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
          <Flex align="center">
            {SoilSightMagicIcon}
            {isSoilPlus3DataGapFeatureActive ? (
              field && (
                <SoilPlus3Menu
                  availablePracticeBundles={practices?.availablePracticeBundles || []}
                  carbonProgram={currentFarm?.carbonProgram ?? null}
                  field={field}
                  hectareLimitWouldBeExceeded={hectareLimitWouldBeExceeded}
                  isEnrolled={isEnrolled}
                  isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
                  placement="right"
                  setShowFieldEditDrawer={(show) => {
                    if (show) onOpenFieldDetails();
                  }}
                  setShowDeleteFieldModal={setShowDeleteFieldModal}
                />
              )
            ) : (
              <FCPOMenu
                availablePracticeBundles={practices?.availablePracticeBundles || []}
                fieldId={fieldId}
                isEnrolled={isEnrolled}
                setShowFieldEditDrawer={(show) => {
                  if (show) onOpenFieldDetails();
                }}
                setShowDeleteFieldModal={setShowDeleteFieldModal}
                fieldDetails={field}
                carbonProgram={currentFarm?.carbonProgram ?? null}
                isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
                placement="right"
              />
            )}
          </Flex>
        </Flex>
        <Flex className="field-cell-layout-bottom">
          <Flex className="field-cell-tags" align="center">
            <FieldParticipationTags
              className="chip-pill capitalize ellipsis"
              availablePracticeBundles={practices?.availablePracticeBundles || []}
              carbonProgram={currentFarm?.carbonProgram}
              activeFarmReportingYear={activeFarmReportingYear}
            />
            {isRedZone && <Tag className="chip-pill capitalize" colour="red" text={t("farm:redzone")} />}
            {hasNoPayoutDueToNoPoints && <NoPayoutTag className="chip-pill capitalize" />}
          </Flex>
          <div className="field-cell-actions">
            <OpenTaskUI
              carbonProgramOutcomesProgress={carbonProgramOutcomesProgress}
              currentPayout={currentPayout}
              carbonProgram={currentFarm?.carbonProgram ?? null}
              handleMissingDataClick={handleMissingDataClick}
              hasNoAllotment={hasNoAllotment}
              isEnrolled={isEnrolled}
              isFetchingCarbonProgramOutcomes={isFetchingCarbonProgramOutcomes}
              openTasksCount={openTasksCount}
              setOpenProgramInfoDrawer={setOpenProgramInfoDrawer}
              shouldShowPayout={shouldShowPayout}
              t={t}
            />
          </div>
        </Flex>
      </Flex>
    </div>
  );

  const oldSubmissionFlowFieldCell = (
    <div className="field-cell-layout">
      <Flex className="field-cell-layout-main">
        <Flex className="field-cell-layout-top">
          <TooltipWrapper
            className="field-cell-tooltip"
            tooltip={
              isPracticeEnrollmentDisabled
                ? {
                    title: (
                      <Typography variant="paragraph-m" className="field-cell-tooltip-content">
                        {hectareLimitWouldBeExceeded ? t("allFieldView:fieldCell.tooltipHaLimit") : t("allFieldView:fieldCell.tooltipPayout")}
                      </Typography>
                    ),
                    placement: "top",
                  }
                : null
            }
            data-testid={"field-cell-tooltip"}
          >
            <Typography variant="paragraph-m" bold className={`field-name ${cellStates.nameError ? cellStates.className : ""}`}>
              {fieldName || cellStates.nameError}
            </Typography>
          </TooltipWrapper>
          <Flex align="center">
            {SoilSightMagicIcon}
            {isSoilPlus3DataGapFeatureActive ? (
              field && (
                <SoilPlus3Menu
                  availablePracticeBundles={practices?.availablePracticeBundles || []}
                  isEnrolled={isEnrolled}
                  setShowFieldEditDrawer={(show) => {
                    if (show) onOpenFieldDetails();
                  }}
                  setShowDeleteFieldModal={setShowDeleteFieldModal}
                  field={field}
                  carbonProgram={currentFarm?.carbonProgram ?? null}
                  hectareLimitWouldBeExceeded={hectareLimitWouldBeExceeded}
                  isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
                  placement="right"
                />
              )
            ) : (
              <FCPOMenu
                availablePracticeBundles={practices?.availablePracticeBundles || []}
                fieldId={fieldId}
                isEnrolled={isEnrolled}
                setShowFieldEditDrawer={(show) => {
                  if (show) onOpenFieldDetails();
                }}
                setShowDeleteFieldModal={setShowDeleteFieldModal}
                fieldDetails={field}
                carbonProgram={currentFarm?.carbonProgram ?? null}
                isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
                placement="right"
              />
            )}
            {isEnrolled && currentFarm?.carbonProgram?.fieldsManagement?.fieldEnrollmentType !== ENROLLMENT_TYPE.auto && (
              <Icon iconName="tabler/certificate" className="icon inline klim-icon" dataTestId="field-cell-enrollment-icon" />
            )}
          </Flex>
        </Flex>
      </Flex>
      <Flex className="field-cell-layout-bottom">
        <div className="bottom-text">
          {cellStates.locationError ? (
            <span className={cellStates.locationError ? cellStates.className : ""}>{cellStates.locationError}</span>
          ) : (
            <>
              <Typography variant="paragraph-m" color="grey-600">
                {formatNumber({ number: size })} ha
              </Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {isRedZone && t("farm:redzone")}
              </Typography>
              {hasNoPayoutDueToNoPoints && <NoPayoutTag />}
              <HideFCPOFeatureForUnenrolledFarms>
                {isEnrolled && hasFieldOutcomes && !isInventoryProgram ? (
                  areOutcomesValid && currentPayout !== null ? (
                    <Typography variant="paragraph-s" bold data-testid="field-payouts" color="green-400">
                      {formatNumericCurrency(currentPayout, { currency: currentFarm?.carbonProgram?.currency })}
                    </Typography>
                  ) : (
                    <Button className="field-cell-button" size="small" type="secondary" onClick={handleMissingDataClick} dataTestId="add-data-btn">
                      {t("allFieldView:fieldCell.addData")}
                    </Button>
                  )
                ) : null}
              </HideFCPOFeatureForUnenrolledFarms>
            </>
          )}
        </div>
      </Flex>
    </div>
  );

  return isLoadingAllFieldsPractices ? (
    <Flex align="center" justify="center" style={{ height: "100px" }}>
      <Spin />
    </Flex>
  ) : (
    <div className="width-100" style={{ height: "100%" }}>
      <Card
        onClick={() => {
          onOpenFieldDetails();
          _onClick?.();
        }}
        className={`field-cell ${className}`}
        mixpanelText="field-cell"
        dataTestId="field-cell"
      >
        <div className="field-cell-content">
          <Flex align="center" className="field-cell-inner">
            {hasLocation && (
              <div className="field-cell-map-preview">
                {geometry && (
                  <ErrorBoundary>
                    <MapStatic field={field} geometry={geometry} location={field?.location} />
                  </ErrorBoundary>
                )}
              </div>
            )}
            {isSubmissionFeatureEnabled ? newSubmissionFlowFieldCell : oldSubmissionFlowFieldCell}
          </Flex>
        </div>
      </Card>
      {currentFarm?.carbonProgram && (
        <ExtraInfo visible={openProgramInfoDrawer} type={currentFarm.carbonProgram.programSlug} setVisible={setOpenProgramInfoDrawer} />
      )}
      <Modal
        showModal={showDeleteFieldModal}
        onOk={() => {
          onModalSubmit();
          setShowDeleteFieldModal(false);
        }}
        onCancel={() => {
          setShowDeleteFieldModal(false);
        }}
        okText={t("farm:deleteModalConfirm.deleteText")}
        cancelText={t("farm:deleteModalConfirm.cancel")}
        zIndex={9999}
        destroyOnHidden={true}
        title={
          <>
            <Icon iconName="tabler/alert-circle" className="inline icon margin-right-75" size="m" fill="#DB3804" color="#FFF" />
            <Typography variant="paragraph-m" className="headline-3">
              {t("farm:deleteModalConfirm.fieldTitle", { fieldName: fieldName })}
            </Typography>
          </>
        }
        content={<Typography variant="paragraph-m">{t("farm:deleteModalConfirm.fieldMessage", { fieldName: fieldName })}</Typography>}
      />
    </div>
  );
};
